import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/shared/services/firebase';
import type { Order } from '../types/orders';

export interface NotificationState {
    unreadCount: number;
    incidents: Order[];
    lastIncidentTime: number | null;
    isMuted: boolean;
}

export function useIncidentsListener() {
    // Leer estado inicial de mute desde localStorage
    const [isMuted, setIsMuted] = useState(() => {
        const saved = localStorage.getItem('notifications_muted');
        return saved === 'true';
    });

    const [state, setState] = useState<Omit<NotificationState, 'isMuted'>>({
        unreadCount: 0,
        incidents: [],
        lastIncidentTime: null
    });

    // Referencia para no reproducir sonido en la carga inicial
    const isFirstLoad = useRef(true);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const isMutedRef = useRef(isMuted);

    // Sincronizar ref con state para el callback de onSnapshot
    useEffect(() => {
        isMutedRef.current = isMuted;
        localStorage.setItem('notifications_muted', String(isMuted));
    }, [isMuted]);

    useEffect(() => {
        // Inicializar audio
        audioRef.current = new Audio('/audio/notificacion.mp3');

        // Query: Pedidos con incidencias abiertas
        const q = query(
            collection(db, 'orders'),
            where('incident.status', '==', 'OPEN'),
            where('status', '!=', 'Cancelado')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const incidents = snapshot.docs.map(doc => ({
                orderId: doc.id,
                ...doc.data()
            } as Order));

            // Ordenar por fecha de creación de la incidencia (descendente)
            incidents.sort((a, b) => {
                const timeA = a.incident?.createdAt?.seconds ?? 0;
                const timeB = b.incident?.createdAt?.seconds ?? 0;
                return timeB - timeA;
            });

            setState(prev => {
                // Detectar si hay NUEVAS incidencias para reproducir sonido
                if (!isFirstLoad.current && incidents.length > prev.incidents.length) {
                    if (!isMutedRef.current) {
                        playSound();
                    }
                }

                isFirstLoad.current = false;

                return {
                    unreadCount: incidents.length,
                    incidents,
                    lastIncidentTime: incidents[0]?.incident?.createdAt?.seconds ?? null
                };
            });
        }, (error) => {
            console.error("Error listening to incidents:", error);
        });

        return () => unsubscribe();
    }, []);

    const playSound = () => {
        if (audioRef.current) {
            audioRef.current.play().catch(e => console.log("Error reproducing audio:", e));
        }
    };

    const toggleMute = () => {
        setIsMuted(prev => !prev);
    };

    return {
        ...state,
        isMuted,
        toggleMute
    };
}
